import React, { useEffect, useRef } from "react";
import GEO_DATA from "../data/ne_110m_land.json";

const TWO_PI = Math.PI * 2;
const RAD = Math.PI / 180;

// Convert the Natural Earth coastline once. Frames only rotate these vectors.
const LAND_RINGS = GEO_DATA.features.flatMap(({ geometry }) => {
  const rings =
    geometry.type === "Polygon"
      ? geometry.coordinates
      : geometry.type === "MultiPolygon"
        ? geometry.coordinates.flat()
        : [];
  return rings.map((ring) =>
    ring.map(([longitude, latitude]) => {
      const phi = latitude * RAD;
      const lambda = longitude * RAD;
      return {
        x: Math.cos(phi) * Math.sin(lambda),
        y: Math.sin(phi),
        z: Math.cos(phi) * Math.cos(lambda),
      };
    }),
  );
});

function horizonIntersection(a, b, cx, cy, radius) {
  const fraction = a.z / (a.z - b.z);
  const x = a.x + (b.x - a.x) * fraction;
  const y = a.y + (b.y - a.y) * fraction;
  const length = Math.hypot(x, y) || 1;
  return { x: cx + (x / length) * radius, y: cy - (y / length) * radius };
}

function landPaths(yaw, cx, cy, radius) {
  const fill = new Path2D();
  const coast = new Path2D();
  const cosYaw = Math.cos(yaw);
  const sinYaw = Math.sin(yaw);

  for (const ring of LAND_RINGS) {
    const points = ring.map((point) => ({
      x: point.x * cosYaw + point.z * sinYaw,
      y: point.y,
      z: point.z * cosYaw - point.x * sinYaw,
    }));
    const start = points.findIndex((point) => point.z >= 0);
    if (start === -1) continue;
    const first = points[start];
    fill.moveTo(cx + first.x * radius, cy - first.y * radius);
    coast.moveTo(cx + first.x * radius, cy - first.y * radius);
    let exitAngle = null;

    for (let step = 1; step <= points.length; step += 1) {
      const previous = points[(start + step - 1) % points.length];
      const point = points[(start + step) % points.length];
      if (previous.z >= 0 && point.z < 0) {
        const exit = horizonIntersection(previous, point, cx, cy, radius);
        fill.lineTo(exit.x, exit.y);
        coast.lineTo(exit.x, exit.y);
        exitAngle = Math.atan2(exit.y - cy, exit.x - cx);
      } else if (previous.z < 0 && point.z >= 0) {
        const entry = horizonIntersection(previous, point, cx, cy, radius);
        const entryAngle = Math.atan2(entry.y - cy, entry.x - cx);
        if (exitAngle !== null) {
          let sweep = entryAngle - exitAngle;
          if (sweep > Math.PI) sweep -= TWO_PI;
          if (sweep < -Math.PI) sweep += TWO_PI;
          // Follow the sphere's limb, rather than cutting a straight chord
          // through a continent when its coastline crosses the horizon.
          fill.arc(cx, cy, radius, exitAngle, exitAngle + sweep, sweep < 0);
        }
        fill.lineTo(cx + point.x * radius, cy - point.y * radius);
        coast.moveTo(entry.x, entry.y);
        coast.lineTo(cx + point.x * radius, cy - point.y * radius);
        exitAngle = null;
      } else if (point.z >= 0) {
        fill.lineTo(cx + point.x * radius, cy - point.y * radius);
        coast.lineTo(cx + point.x * radius, cy - point.y * radius);
      }
    }
    fill.closePath();
  }
  return { fill, coast };
}

export default function GlobeCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!ctx) return undefined;

    const width = canvas.width;
    const height = canvas.height;
    const cx = width / 2;
    const cy = height / 2;
    const radius = width * 0.342;
    const motionPreference = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    );
    let frameId = null;
    let visible = true;
    let yaw = -0.24;
    let last = performance.now();

    function draw() {
      ctx.clearRect(0, 0, width, height);

      const atmosphere = ctx.createRadialGradient(
        cx,
        cy,
        radius * 0.83,
        cx,
        cy,
        radius * 1.34,
      );
      atmosphere.addColorStop(0, "rgba(91,166,218,0)");
      atmosphere.addColorStop(0.63, "rgba(87,154,223,0.16)");
      atmosphere.addColorStop(0.82, "rgba(135,109,205,0.08)");
      atmosphere.addColorStop(1, "rgba(135,109,205,0)");
      ctx.fillStyle = atmosphere;
      ctx.fillRect(0, 0, width, height);

      // Orbital guides are decorative; they do not represent satellite feeds.
      ctx.lineWidth = 1.2;
      ctx.strokeStyle = "rgba(88,109,155,0.18)";
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 75, 0, TWO_PI);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(cx, cy, radius + 110, -0.7, 1.9);
      ctx.strokeStyle = "rgba(115,97,179,0.18)";
      ctx.stroke();

      ctx.save();
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, TWO_PI);
      ctx.clip();
      const ocean = ctx.createRadialGradient(
        cx - radius * 0.35,
        cy - radius * 0.4,
        10,
        cx,
        cy,
        radius * 1.1,
      );
      ocean.addColorStop(0, "#337ec0");
      ocean.addColorStop(0.5, "#236ba8");
      ocean.addColorStop(0.86, "#194e88");
      ocean.addColorStop(1, "#15375f");
      ctx.fillStyle = ocean;
      ctx.fillRect(0, 0, width, height);

      const graticule = new Path2D();
      const projectLine = (positions) => {
        let penDown = false;
        for (const [longitude, latitude] of positions) {
          const phi = latitude * RAD;
          const lambda = longitude * RAD + yaw;
          const depth = Math.cos(phi) * Math.cos(lambda);
          if (depth >= 0) {
            const x = cx + Math.cos(phi) * Math.sin(lambda) * radius;
            const y = cy - Math.sin(phi) * radius;
            if (penDown) graticule.lineTo(x, y);
            else graticule.moveTo(x, y);
            penDown = true;
          } else penDown = false;
        }
      };
      for (let lat = -60; lat <= 60; lat += 30) {
        projectLine(Array.from({ length: 121 }, (_, i) => [-180 + i * 3, lat]));
      }
      for (let lon = -180; lon < 180; lon += 30) {
        projectLine(Array.from({ length: 61 }, (_, i) => [lon, -90 + i * 3]));
      }
      ctx.strokeStyle = "rgba(201,231,250,0.14)";
      ctx.lineWidth = 1;
      ctx.stroke(graticule);

      const land = landPaths(yaw, cx, cy, radius);
      const landColor = ctx.createLinearGradient(
        cx - radius,
        cy - radius,
        cx + radius,
        cy + radius,
      );
      landColor.addColorStop(0, "#bce8d9");
      landColor.addColorStop(0.5, "#82c8b9");
      landColor.addColorStop(1, "#4a9ea4");
      ctx.fillStyle = landColor;
      ctx.fill(land.fill, "evenodd");
      ctx.strokeStyle = "rgba(207,247,235,0.7)";
      ctx.lineWidth = 0.85;
      ctx.stroke(land.coast);

      const shade = ctx.createLinearGradient(
        cx - radius,
        cy - radius * 0.35,
        cx + radius,
        cy + radius * 0.3,
      );
      shade.addColorStop(0, "rgba(239,250,255,0.08)");
      shade.addColorStop(0.55, "rgba(12,34,67,0)");
      shade.addColorStop(1, "rgba(12,29,61,0.43)");
      ctx.fillStyle = shade;
      ctx.fillRect(0, 0, width, height);
      ctx.restore();

      const rim = ctx.createLinearGradient(
        cx - radius,
        cy - radius,
        cx + radius,
        cy + radius,
      );
      rim.addColorStop(0, "rgba(211,235,255,0.95)");
      rim.addColorStop(0.5, "rgba(135,180,225,0.38)");
      rim.addColorStop(1, "rgba(113,96,182,0.6)");
      ctx.beginPath();
      ctx.arc(cx, cy, radius, 0, TWO_PI);
      ctx.strokeStyle = rim;
      ctx.lineWidth = 2.5;
      ctx.stroke();

      ctx.save();
      ctx.translate(cx, cy);
      ctx.rotate(-0.42);
      ctx.beginPath();
      ctx.ellipse(0, 0, radius + 86, (radius + 86) * 0.4, 0, 0, Math.PI);
      ctx.strokeStyle = "rgba(131,111,186,0.37)";
      ctx.lineWidth = 1.4;
      ctx.stroke();
      const orbitPosition = 0.86;
      ctx.beginPath();
      ctx.arc(
        Math.cos(orbitPosition) * (radius + 86),
        Math.sin(orbitPosition) * (radius + 86) * 0.4,
        6,
        0,
        TWO_PI,
      );
      ctx.fillStyle = "#8a74c4";
      ctx.fill();
      ctx.restore();
    }

    function shouldAnimate() {
      return visible && !document.hidden && !motionPreference.matches;
    }

    function tick(now) {
      frameId = null;
      if (!shouldAnimate()) return;
      const elapsed = Math.min(50, now - last) / 1000;
      last = now;
      // Positive yaw moves the visible surface left to right: Earth's
      // eastward rotation when viewed with north at the top.
      yaw = (yaw + elapsed * 0.1) % TWO_PI;
      draw();
      frameId = requestAnimationFrame(tick);
    }

    function updateAnimation() {
      if (frameId !== null) cancelAnimationFrame(frameId);
      frameId = null;
      last = performance.now();
      draw();
      if (shouldAnimate()) frameId = requestAnimationFrame(tick);
    }

    const observer =
      typeof IntersectionObserver === "function"
        ? new IntersectionObserver(
            ([entry]) => {
              visible = entry.isIntersecting;
              updateAnimation();
            },
            { threshold: 0.05 },
          )
        : null;
    observer?.observe(canvas);
    motionPreference.addEventListener("change", updateAnimation);
    document.addEventListener("visibilitychange", updateAnimation);
    updateAnimation();

    return () => {
      if (frameId !== null) cancelAnimationFrame(frameId);
      observer?.disconnect();
      motionPreference.removeEventListener("change", updateAnimation);
      document.removeEventListener("visibilitychange", updateAnimation);
    };
  }, []);

  return (
    <div
      className="av-globe"
      role="img"
      aria-label="Illustrated Earth rotating eastward, with north at the top"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: 640,
        marginInline: "auto",
        aspectRatio: "1 / 1",
      }}
    >
      <canvas
        ref={canvasRef}
        width={1200}
        height={1200}
        aria-hidden="true"
        style={{ display: "block", width: "100%", height: "100%" }}
      />
    </div>
  );
}
